import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { Theme } from 'ui_system/theme';
import { ReactModal } from 'ui_system/modals/ReactModal';
import ReactLoading from 'react-loading';

export const ModalText = styled.div`
  margin-bottom: 10px;
  color: #333333;
`;

export const EmailLabel = styled.div`
  font-size: 14px;
  color: #6e6e6e;
`;

export const Label = styled.div`
  margin-top: 30px;
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 600;
`;

export const InputSection = styled.div`
  margin-bottom: 25px;
`;

export const Input = styled.input`
  width: 100%;
  font-size: 14px;
  color: #333333;
  padding: 14px 10px;

  box-sizing: border-box;
  border: 1px solid
    ${(props) =>
      props.disabled
        ? Theme.colors.lightBackground
        : Theme.colors.previewSubtleOutlineColor};
  border-radius: 0px;

  background: ${(props) =>
    props.disabled ? Theme.colors.lightBackground : 'white'};

  ::placeholder {
    color: ${Theme.colors.previewGuideTextColor};
  }
`;

export const Error = styled.div`
  padding: 6px 10px;
  color: ${Theme.colors.red};
`;

const Heading = styled.h1`
  text-align: center;
  font-size: 24px;
  color: #333333;
  font-weight: bold;
  margin-top: 0px;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    padding-bottom: 0;
    padding-top: 16px;
    font-size: 18px;
  }
`;

export const ActionSingle = styled.div`
  display: flex;
  padding-bottom: 0px;
`;

export const ActionDual = styled(ActionSingle)`
  justify-content: space-between;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    padding-left: 0px;
    padding-right: 0px;
  }
`;

export const CancelButton = styled('button')`
  cursor: pointer;
  background-color: white;
  border-radius: 4px;
  font-size: 16px;
  padding: 10px;
  justify-self: center;
  border: solid 1px ${Theme.colors.previewSubtleOutlineColor};
  color: ${Theme.colors.previewSecondaryTextColor};
  min-width: 100px;
`;

export const UpdateButton = styled(CancelButton)`
  color: white;
  background-color: ${Theme.colors.green};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  :hover,
  :visited,
  :active {
    color: white;
  }

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    margin-left: 10px;
  }
`;

export const Loading = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 50px;
  margin: 80px;
`;

const ConfirmEmailModalContent = ({ email, onClose, onSubmit, isLoading }) => {
  const intl = useIntl();
  const [displayEmail, setDisplayEmail] = useState(email);
  const [displayError, setDisplayError] = useState(false);

  const updateEmail = (value) => {
    //check if email is valid
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setDisplayError(false);
    } else {
      setDisplayError(true);
    }
    //Update the value in the input box regardless of validity
    setDisplayEmail(value);
  };
  return (
    <Fragment>
      {!isLoading ? (
        <div>
          <Heading className="confirm-email-modal-heading">
            <FormattedMessage
              id="UPDATE_EMAIL_ADDRESS"
              defaultMessage="Update Email address"
            />
          </Heading>
          <ModalText className="confirm-email-modal-text">
            <FormattedMessage
              id="EMAIL_CONFIRMATION_TEXT"
              defaultMessage="Your email address will be updated to the new address that you provide here. The booking confirmation email and future notifications will also be sent to this address."
            />
          </ModalText>
          <InputSection>
            <EmailLabel className="confirm-email-modal-input-label">
              <FormattedMessage
                id="EMAIL_ADDRESS"
                defaultMessage="Email address"
              />
            </EmailLabel>
            <Input
              className="confirm-email-modal-input"
              onChange={(e) => updateEmail(e.target.value)}
              type="email"
              value={displayEmail}
              placeholder={email}
              data-testid="update-email-field"
            />
            {displayError ? (
              <Error>
                <FormattedMessage
                  id="INVALID_EMAIL"
                  defaultMessage="Invalid Email"
                />
              </Error>
            ) : null}
          </InputSection>
          <ActionDual>
            <CancelButton
              onClick={() => onClose(false)}
              data-testid="dont-cancel-btn"
              className="confirm-email-modal-cancel"
            >
              <FormattedMessage id="CANCEL" defaultMessage="Cancel" />
            </CancelButton>
            <UpdateButton
              onClick={() => onSubmit(displayEmail)}
              data-testid="update-email-btn"
              disabled={email === displayEmail || displayError}
              className="confirm-email-modal-update brand-bg"
            >
              <FormattedMessage
                id="UPDATE_EMAIL_ADDRESS"
                defaultMessage="Update Email address"
              />
            </UpdateButton>
          </ActionDual>
        </div>
      ) : (
        <Loading className="confirm-email-modal-loading brand-color">
          <ReactLoading type="spin" color="#333333" delay={0} />
        </Loading>
      )}
    </Fragment>
  );
};

ConfirmEmailModalContent.propTypes = {
  email: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
};

const CEM = ReactModal(ConfirmEmailModalContent);
export const ConfirmEmailModal = styled(CEM)`
  min-height: 0px;
  width: 600px;
  max-width: 90vw;
  margin: 50px auto;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    width: 100%;
  }
`;
